/**
 * The left navigation rail (P4-9, §0.2 / §0.6).
 *
 * The blueprint has specified this since v1 and the app never had it: Phase 2
 * shipped a flat horizontal tab bar described in its own notes as "deliberately
 * cheap to change", and Phase 3 hung three more pages on that bar. This replaces
 * it with the rail the blueprint actually asks for — a persistent 240px navy
 * column at `lg` and above, an off-canvas drawer below that, gold used only as
 * the active-state marker inside the dark chrome and nowhere else.
 *
 * The grouping is the v1 prototype's five sections (Command, Delivery, Money,
 * Network, System) reconciled against the pages that exist today rather than
 * copied mechanically — the v1 array named pages like /pipeline, /change-orders
 * and /vendors that were never built, and knew nothing of Daily logs,
 * Procurement, Review queue or Merge history.
 *
 * All rail styling comes from the `.nav-rail` / `.nav-item` / `.nav-section-label`
 * contracts in tokens.css, which scripts/check-contrast.mjs measures. Nothing
 * here restates a colour those contracts already own.
 */
import { useEffect, useRef } from 'react';
import type { PillarCode } from '../lib/api';
import type { Route } from '../lib/route';

/** 18px stroke icons, drawn inline so the rail adds no icon dependency. */
type IconName =
  | 'dashboard'
  | 'entities'
  | 'projects'
  | 'logs'
  | 'documents'
  | 'photos'
  | 'procurement'
  | 'schedule'
  | 'contacts'
  | 'review'
  | 'merges'
  | 'health';

const ICON_PATHS: Record<IconName, JSX.Element> = {
  // A clock over a calendar leaf: the schedule is dates plus lead times.
  schedule: (
    <>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 9h18M8 2v4M16 2v4" />
      <path d="M12 13v3l2 1.5" />
    </>
  ),
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </>
  ),
  entities: (
    <>
      <path d="M3 21h18" />
      <path d="M5 21V7l7-4 7 4v14" />
      <path d="M10 21v-5h4v5" />
    </>
  ),
  projects: (
    <>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      <path d="M9 12v5" />
      <path d="M14 10v7" />
    </>
  ),
  logs: (
    <>
      <path d="M5 3h11l4 4v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M15 3v5h5" />
      <path d="M8 13h8" />
      <path d="M8 17h5" />
    </>
  ),
  // A folder rather than a page: what the library lists is the estate's
  // filing, not a document the platform authored.
  documents: (
    <>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      <path d="M3 10h18" />
    </>
  ),
  // A camera, not a picture frame: the entry is the act of capturing on site,
  // not a gallery of what was captured.
  photos: (
    <>
      <path d="M3 8a2 2 0 0 1 2-2h2.5l1.2-2h6.6l1.2 2H19a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      <circle cx="12" cy="12.5" r="3.2" />
    </>
  ),
  procurement: (
    <>
      <path d="M3 4h2l2.4 11.2a2 2 0 0 0 2 1.6h7.8a2 2 0 0 0 2-1.6L21 8H6" />
      <circle cx="10" cy="20" r="1" />
      <circle cx="18" cy="20" r="1" />
    </>
  ),
  contacts: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M17 8.5a3 3 0 0 1 0 5" />
      <path d="M18 20a5.5 5.5 0 0 0-2-4.2" />
    </>
  ),
  review: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M20 20l-4.5-4.5" />
      <path d="M8.5 11l2 2 4-4" />
    </>
  ),
  merges: (
    <>
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="6" cy="18" r="2.5" />
      <circle cx="18" cy="12" r="2.5" />
      <path d="M6 8.5v7" />
      <path d="M8.5 6.6c4 .8 5.6 2.6 6.7 5" />
      <path d="M8.5 17.4c4-.8 5.6-2.6 6.7-5" />
    </>
  ),
  health: (
    <>
      <path d="M3 12h4l2.5-6 4 12 2.5-6h5" />
    </>
  ),
};

function Icon({ name }: { name: IconName }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="flex-none"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

interface NavItem {
  label: string;
  /** `#/pillar/{code}` needs the active entity, so a few hrefs are computed. */
  href: string | ((pillar: PillarCode) => string);
  icon: IconName;
  /** Route names that light this item up. */
  matches: Route['name'][];
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

/**
 * Command / Delivery / Money / Network / System, mapped onto the real page set.
 *
 * Command is where the day starts and where entity identity is chosen. Delivery
 * is the work itself. Money is anything that commits spend. Network is the
 * people record and the two screens that keep it trustworthy — the review queue
 * and the merge history are contact stewardship, so they sit with contacts
 * rather than under System.
 *
 * The v1 prototype's fifth group, System, held /integrations and /team. Neither
 * page exists yet and neither has a route, so the group is not rendered: a
 * heading with nothing under it teaches the reader that the rail is unfinished.
 * The Dropbox, Calendar and Weather adapters (P4-3, P4-5, P4-8) are what will
 * fill it, and the group returns with them.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Command',
    items: [
      { label: 'Dashboard', href: '#/', icon: 'dashboard', matches: ['dashboard'] },
      {
        label: 'Entities',
        href: (pillar) => `#/pillar/${pillar}`,
        icon: 'entities',
        matches: ['pillar'],
      },
      // The schedule spans every entity and every project, so it sits in
      // Command beside the dashboard rather than under Delivery.
      { label: 'Schedule', href: '#/schedule', icon: 'schedule', matches: ['schedule'] },
    ],
  },
  {
    label: 'Delivery',
    items: [
      { label: 'Projects', href: '#/projects', icon: 'projects', matches: ['projects', 'project'] },
      { label: 'Daily logs', href: '#/logs', icon: 'logs', matches: ['logs'] },
      // Documents sit in Delivery rather than in System: what they hold is
      // project content, and the System group stays hidden until P4-5 and P4-8
      // give it integrations that are genuinely system-level.
      { label: 'Documents', href: '#/documents', icon: 'documents', matches: ['documents'] },
      // Capture sits beside the library rather than inside it: filing a photo
      // from the field is a different act from reading the estate's filing,
      // and on a phone it has to be reachable in one tap.
      { label: 'Site photos', href: '#/photos', icon: 'photos', matches: ['photos'] },
    ],
  },
  {
    label: 'Money',
    items: [
      {
        label: 'Procurement',
        href: '#/procurement',
        icon: 'procurement',
        matches: ['procurement'],
      },
    ],
  },
  {
    label: 'Network',
    items: [
      { label: 'Contacts', href: '#/contacts', icon: 'contacts', matches: ['contacts'] },
      { label: 'Review queue', href: '#/review', icon: 'review', matches: ['review'] },
      { label: 'Merge history', href: '#/merges', icon: 'merges', matches: ['merges'] },
    ],
  },
];

/** The mark at the top of the rail: navy chrome, so gold is allowed here. */
function RailBrand({ entityLine }: { entityLine: string }): JSX.Element {
  return (
    <div className="border-b border-white/10 px-4 py-4">
      <div className="flex items-center gap-2.5">
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          className="flex-none"
        >
          <path d="M12 2.5 21 8v8l-9 5.5L3 16V8Z" stroke="hsl(var(--brand-gold))" strokeWidth="1.5" />
          <path d="M12 8.2 16 10.5v3.2L12 16l-4-2.3v-3.2Z" fill="hsl(var(--brand-gold))" />
        </svg>
        <span className="wordmark text-h3 font-semibold text-white">CALM Ops</span>
      </div>
      <p className="nav-section-label mt-2 truncate">{entityLine}</p>
    </div>
  );
}

interface ListProps {
  route: Route;
  pillar: PillarCode;
  /** Called after any item is followed, so the drawer can close itself. */
  onNavigate?: () => void;
}

/** The item list, shared by the persistent rail and the drawer. */
function NavList({ route, pillar, onNavigate }: ListProps): JSX.Element {
  return (
    <nav aria-label="Sections" className="flex-1 overflow-y-auto px-2 py-4">
      {NAV_GROUPS.map((group) => (
        <div key={group.label} className="mb-5">
          <p className="nav-section-label px-3 pb-1.5">{group.label}</p>
          <ul>
            {group.items.map((item) => {
              const href = typeof item.href === 'function' ? item.href(pillar) : item.href;
              const current = item.matches.includes(route.name);
              return (
                <li key={item.label}>
                  <a
                    href={href}
                    onClick={onNavigate}
                    aria-current={current ? 'page' : undefined}
                    // The active item is marked by a 3px gold left border from
                    // tokens.css. Inactive items carry the same width as a
                    // transparent border so nothing shifts sideways on selection.
                    className={`nav-item flex items-center gap-3 rounded-r-card py-2 pr-3 text-body ${
                      current ? 'pl-[9px]' : 'border-l-[3px] border-transparent pl-[9px]'
                    }`}
                  >
                    <Icon name={item.icon} />
                    <span className="truncate">{item.label}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

interface RailProps extends ListProps {
  entityLine: string;
}

/** The persistent column. Hidden below `lg`, where the drawer takes over. */
export function NavigationRail(props: RailProps): JSX.Element {
  return (
    <aside className="nav-rail fixed inset-y-0 left-0 z-30 hidden flex-col lg:flex">
      <RailBrand entityLine={props.entityLine} />
      <NavList {...props} />
    </aside>
  );
}

interface DrawerProps extends RailProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The off-canvas drawer for every width below `lg`.
 *
 * §0.6 names 768px as the point where the rail leaves the screen, and `lg` as
 * the point where it is persistent, which leaves 768–1023px unstated. The
 * drawer covers that band rather than inventing a third behaviour for it: a
 * 240px fixed column against a 768px viewport takes a third of the width, which
 * is the reason the spec moves it off-canvas in the first place.
 */
export function NavigationDrawer({ open, onClose, ...rest }: DrawerProps): JSX.Element | null {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    panel.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 lg:hidden">
      <div
        className="absolute inset-0 bg-navy/60"
        role="presentation"
        onClick={onClose}
        data-testid="drawer-scrim"
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Sections"
        tabIndex={-1}
        className="nav-rail absolute inset-y-0 left-0 flex flex-col outline-none"
      >
        <div className="relative">
          <RailBrand entityLine={rest.entityLine} />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="absolute right-2 top-3 flex min-h-touch items-center rounded-card px-3 text-white/75 hover:bg-white/10 hover:text-white"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        <NavList {...rest} />
      </div>
    </div>
  );
}

/** The trigger that opens the drawer. Only present below `lg`. */
export function NavMenuButton({ onOpen }: { onOpen: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label="Open navigation"
      className="rounded-card border border-white/40 px-3 text-white hover:bg-white/10 lg:hidden"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      >
        <path d="M4 7h16M4 12h16M4 17h16" />
      </svg>
    </button>
  );
}
